/**
 * Phase I Wave 2.5 · Plan Feature Gating · SSOT (frontend side)
 * ─────────────────────────────────────────────────────────────
 * Mirror of `backend/services/phase_i_wave2_5/feature_gating.py`.
 * Any change here MUST be synced in the backend file (and viceversa);
 * `test_wave25_feature_gating_core.py` and `feature_gate.test.jsx`
 * exercise overlapping assertions so a drift is caught at CI time.
 *
 * Hierarchy
 * ---------
 * trial < starter < growth < scale < enterprise
 *
 * Legacy note
 * -----------
 * The old V12 `plan_id = "free"` row is normalized to `"trial"` for the
 * purposes of W2.5 gating · we never want a legacy "free" account to
 * silently inherit higher-tier features.
 *
 * Admin bypass (R2)
 * -----------------
 * Users with role ``"admin"`` or ``"super_admin"`` are mapped to the
 * ``"enterprise"`` effective plan via `userEffectivePlan()` · the
 * bypass is detectable at the UI layer via `bypassReason()`.
 */

export const FEATURE_GATING_V13 = {
    // Core modules
    "lead_engine_full": "starter",         // trial = limited
    "crm_base": "trial",                   // all plans
    "crm_contacts_5000": "growth",         // starter cap 500
    "crm_contacts_unlimited": "scale",
    "outreach_email_send": "trial",        // all plans (different caps)
    "pipeline_kanban_unlimited": "growth",

    // AI
    "ai_chat": "trial",                    // all plans
    "ai_agents_preconfigured": "growth",   // 5 agents
    "ai_agents_full": "scale",             // 15 agents + Multi-Agent Orchestration
    "ai_economy_tokenomics": "scale",
    "ai_universe_dedicated": "enterprise",
    "ai_civilization": "enterprise",

    // Phase I Wave 4.1 · RAG + Vector DB (lazy mock-first)
    "rag_chat": "growth",                  // retrieval-enhanced LLM chat
    "rag_document_upload": "growth",       // PDF/MD/TXT/HTML ingestion
    "rag_web_crawl": "scale",              // URL → embeddings (premium)
    "rag_email_sync": "scale",             // Gmail/Outlook OAuth (Wave 4.1 R2)

    // Phase I Wave 5 · Lead Enrichment (Apollo / Hunter / NeverBounce · MOCK lazy)
    "email_verification_single": "growth", // Hunter verify singolo
    "email_verification_bulk": "growth",   // NeverBounce batch

    // SEO & Content
    "seo_master_base": "growth",           // topic discovery + schema markup
    "seo_master_full": "scale",            // GEO + AEO + internal linking
    "blog_engine_10": "growth",            // 10 articles/month
    "blog_engine_50": "scale",             // 50/month

    // Lead enrichment
    "lead_enrichment_apollo_hunter": "scale",

    // Phase I Wave 3.1 · social lead discovery (Reddit, LinkedIn basic)
    "lead_discovery_social": "growth",
    // Phase I Wave 3.2 · content discovery · split by surface
    "content_discovery_youtube": "growth",      // YouTube Data API
    "content_discovery_blog": "growth",         // Medium
    "content_discovery_newsletter": "scale",    // Substack (premium)
    // Phase I Wave 3.3 · B2C social discovery · 5 providers
    "b2c_discovery_twitter": "growth",
    "b2c_discovery_facebook": "growth",
    "b2c_discovery_instagram": "growth",
    "b2c_discovery_tiktok": "scale",            // TikTok Business is premium
    "b2c_discovery_quora": "growth",

    // Integrations
    "api_access_base": "growth",
    "api_full_webhooks": "scale",

    // Marketplace
    "marketplace_buy_templates": "starter",
    "marketplace_buy_sell": "growth",
    // Phase II Wave B.1/B.2 · marketplace keys.
    "marketplace_browse":         "starter",
    "marketplace_create_listing": "growth",
    "marketplace_pack_creation":  "scale",
    "marketplace_purchase":       "starter",
    "marketplace_preview_run":    "starter",
    "marketplace_write_review":        "starter",
    "marketplace_review_vote_helpful": "starter",

    // Multilang
    "multilang_10": "starter",
    "multilang_40": "growth",

    // Affiliate
    "affiliate_advanced": "scale",

    // P0 FASE F · Advanced analytics (Funnels + Cohort retention)
    "analytics_advanced": "scale",

    // P0 FASE G · Automations builder (CRUD · engine queue iter 2)
    "automations_builder": "growth",

    // White-label
    "white_label_base": "scale",
    "white_label_full": "enterprise",

    // Enterprise-only
    "sso_saml": "enterprise",
    "scim_provisioning": "enterprise",
    "multi_region": "enterprise",
    "data_residency": "enterprise",
    "soc2_compliance": "enterprise",
    "account_manager": "enterprise",

    // Support tiers
    "support_48h": "starter",
    "support_24h": "growth",
    "support_12h": "scale",
    "support_dedicated": "enterprise",
};

export const PLAN_HIERARCHY = ["trial", "starter", "growth", "scale", "enterprise"];

// Wave 2.5 R2 · roles that bypass feature-plan gating → effective enterprise.
export const ADMIN_ROLES = ["admin", "super_admin"];


/** Normalize a user plan id · maps legacy "free" to "trial". */
export function normalizePlan(planId) {
    const raw = (planId || "").toString().toLowerCase();
    if (!raw || raw === "free") return "trial";
    if (PLAN_HIERARCHY.includes(raw)) return raw;
    return "trial";
}


/** True when the user document has an admin / super_admin role. */
export function isAdminRole(user) {
    if (!user || !user.role) return false;
    return ADMIN_ROLES.includes(String(user.role).toLowerCase());
}


/** RAW plan slug · prefer `userEffectivePlan` for gating decisions. */
export function rawUserPlan(user) {
    if (!user) return "trial";
    return normalizePlan(
        user.plan || user.plan_id || (user.subscription && user.subscription.plan),
    );
}


/**
 * Admin-aware effective plan · staff → "enterprise", else raw plan.
 * This is the function every gating check should use.
 */
export function userEffectivePlan(user) {
    if (isAdminRole(user)) return "enterprise";
    return rawUserPlan(user);
}


/** `"admin_role"` when the user bypasses gating via role, else `null`. */
export function bypassReason(user) {
    return isAdminRole(user) ? "admin_role" : null;
}


/** `true` when `userPlanId` meets or exceeds `requiredPlan` (RAW compare). */
export function hasPlanAccess(userPlanId, requiredPlan) {
    const u = PLAN_HIERARCHY.indexOf(normalizePlan(userPlanId));
    const r = PLAN_HIERARCHY.indexOf((requiredPlan || "").toLowerCase());
    if (u === -1 || r === -1) return false;
    return u >= r;
}


/**
 * `true` when the user can use `featureKey`.
 * Admin bypass applied BEFORE the plan hierarchy check.
 *
 * NOTE · for backward compatibility the first argument accepts BOTH:
 *   - a user object (preferred · includes role for admin bypass)
 *   - a plan slug string (legacy · no bypass possible)
 */
export function canUseFeature(userOrPlan, featureKey) {
    const required = FEATURE_GATING_V13[featureKey];
    if (!required) return true;   // non-gated feature · safe default
    // String fallback → treat as plan slug, no bypass.
    if (typeof userOrPlan === "string") {
        return hasPlanAccess(userOrPlan, required);
    }
    return hasPlanAccess(userEffectivePlan(userOrPlan), required);
}


/** Returns the required plan slug for a feature, or null for unknown. */
export function requiredPlanFor(featureKey) {
    return FEATURE_GATING_V13[featureKey] || null;
}
